
import React, { useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs from "dayjs";
import { Box } from "@mui/material";

import useCalendarData from "./hooks/useCalendarData";
import CustomEvent from "./components/CustomEvent";
import CustomDateCellWrapper from "./components/CustomDateCellWrapper";
import Legend from "./components/Legend";
import AddEventModal from "./components/AddEventModal";
import EditEventModal from "./components/EditEventModal";

import { addEvent, editEvent, deleteEvent } from "../../utils/api";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const Calendar = ({
  workOrderId = null,
  violation_id = null,
  architectural_request_id = null,
}) => {
  const [currentView, setCurrentView] = useState("month");
  const { events, setEvents, users } = useCalendarData(
    workOrderId,
    violation_id,
    architectural_request_id
  );

  const [newEventModalOpen, setNewEventModalOpen] = useState(false);
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);

  const [newEvent, setNewEvent] = useState({
    title: "",
    start: null,
    end: null,
    assigned_to_user_id: "",
    description: "",
  });

  const [selectedEvent, setSelectedEvent] = useState(null);

  const eventPropGetter = (event) => {
    let style = {
      borderRadius: "8px",
      padding: "2px 6px",
      boxShadow: "none",
    };

    if (event.violation_id) {
      style = {
        ...style,
        border: "2px solid #d32f2f",
        backgroundColor: "#fff5f5",
        color: "#b71c1c",
        fontWeight: "500",
      };
    } else if (event.architectural_request_id) {
      style = {
        ...style,
        border: "2px solid #FFD700",
        backgroundColor: "#fffef5",
        color: "#8a6d00",
        fontWeight: "500",
      };
    } else if (event.work_order_id) {
      style = {
        ...style,
        border: "2px solid #3174ad",
        backgroundColor: "#f5f9ff",
        color: "#0d47a1",
        fontWeight: "500",
      };
    }

    return { style };
  };

  const dayPropGetter = (date) => {
    const isToday = dayjs(date).isSame(dayjs(), "day");
    return isToday
      ? {
          style: {
            border: "1px solid #2f63ad",
            borderRadius: "8px",
            backgroundColor: "#f8fbff",
          },
        }
      : {};
  };

  const handleAddEvent = async () => {
    const currentUserId =
      localStorage.getItem("userId") || sessionStorage.getItem("userId");

    if (!currentUserId) {
      alert("User not identified. Please login again.");
      return;
    }

    const { title, start, end, assigned_to_user_id } = newEvent;

    if (!title || !start || !end || !assigned_to_user_id) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      const response = await addEvent({
        work_order_id: workOrderId,
        violation_id,
        architectural_request_id,
        title,
        description: newEvent.description || "No description provided",
        start_time: start,
        end_time: end,
        created_by_user_id: currentUserId,
        assigned_to_user_id,
      });

      setEvents((prev) => [
        ...prev,
        { ...newEvent, id: response.data.id, architectural_request_id },
      ]);

      setNewEvent({
        title: "",
        start: null,
        end: null,
        assigned_to_user_id: "",
        description: "",
      });

      setNewEventModalOpen(false);
      alert("Event added successfully!");
    } catch (error) {
      console.error("Add event failed:", error);
      alert("Failed to add event.");
    }
  };

  const handleEditEvent = async () => {
    if (!selectedEvent) return;

    try {
      await editEvent(selectedEvent.id, {
        title: selectedEvent.title,
        description: selectedEvent.description,
        start_time: selectedEvent.start,
        end_time: selectedEvent.end,
        assigned_to_user_id: selectedEvent.assigned_to_user_id,
      });

      setEvents((prev) =>
        prev.map((event) =>
          event.id === selectedEvent.id ? selectedEvent : event
        )
      );

      setEditEventModalOpen(false);
      alert("Event updated successfully!");
    } catch (error) {
      console.error("Edit event failed:", error);
      alert("Failed to update event.");
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      await deleteEvent(selectedEvent.id);
      setEvents((prev) => prev.filter((event) => event.id !== selectedEvent.id));
      setEditEventModalOpen(false);
      alert("Event deleted successfully!");
    } catch (error) {
      console.error("Delete event failed:", error);
      alert("Failed to delete event.");
    }
  };

  const handleSlotClick = (slotInfo) => {
    setNewEvent({ ...newEvent, start: slotInfo.start, end: slotInfo.end });
    setNewEventModalOpen(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent({
      id: event.id,
      title: event.title,
      description: event.description,
      assigned_to_user_id: event.assigned_to_user_id,
      start: event.rawStart,
      end: event.rawEnd,
    });
    setEditEventModalOpen(true);
  };

  return (
    <Box
      sx={{
        height: 600,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        "& .rbc-calendar": {
          fontFamily: "inherit",
          color: "#223142",
        },
        "& .rbc-toolbar": {
          marginBottom: "16px",
          gap: "10px",
          flexWrap: "wrap",
        },
        "& .rbc-toolbar button": {
          border: "1px solid rgba(47,99,173,0.14)",
          background: "#fff",
          color: "#2f63ad",
          borderRadius: "10px",
          padding: "6px 12px",
          fontWeight: 600,
          textTransform: "none",
          boxShadow: "none",
        },
        "& .rbc-toolbar button:hover": {
          background: "rgba(47,99,173,0.08)",
          borderColor: "rgba(47,99,173,0.22)",
        },
        "& .rbc-toolbar button.rbc-active": {
          background: "#2f63ad",
          color: "#fff",
          borderColor: "#2f63ad",
        },
        "& .rbc-toolbar-label": {
          fontSize: "1rem",
          fontWeight: 700,
          color: "#223142",
        },
        "& .rbc-header": {
          padding: "10px 6px",
          fontWeight: 700,
          fontSize: "0.9rem",
          color: "#4a5568",
          backgroundColor: "#f8fbff",
          borderBottom: "1px solid #e8eef5",
        },
        "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
          border: "1px solid #e8eef5",
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: "#fff",
        },
        "& .rbc-date-cell": {
          paddingRight: "8px",
          fontWeight: 500,
          color: "#5f6b7a",
        },
        "& .rbc-off-range-bg": {
          backgroundColor: "#fafcff",
        },
        "& .rbc-today": {
          backgroundColor: "#f7fbff",
        },
        "& .rbc-time-content, & .rbc-month-row, & .rbc-day-bg, & .rbc-timeslot-group":
          {
            borderColor: "#edf2f7",
          },
        "& .rbc-agenda-table": {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
        "& .rbc-agenda-table thead > tr > th": {
          backgroundColor: "#f8fbff",
          color: "#4a5568",
          fontWeight: 700,
          borderBottom: "1px solid #e8eef5",
          padding: "10px",
        },
        "& .rbc-agenda-table tbody > tr > td": {
          padding: "10px",
          borderBottom: "1px solid #eef2f7",
          color: "#223142",
        },
      }}
    >
      <Legend />

      <BigCalendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 550 }}
        view={currentView}
        onView={setCurrentView}
        views={["month", "week", "day", "agenda"]}
        defaultView="agenda"
        popup
        selectable
        onSelectSlot={handleSlotClick}
        onSelectEvent={handleEventClick}
        eventPropGetter={eventPropGetter}
        dayPropGetter={dayPropGetter}
        defaultDate={new Date()}
        components={{
          event: (props) => (
            <CustomEvent {...props} currentView={currentView} />
          ),
          dateCellWrapper: (props) => (
            <CustomDateCellWrapper
              {...props}
              events={events}
              currentView={currentView}
            />
          ),
        }}
      />

      <AddEventModal
        open={newEventModalOpen}
        onClose={() => setNewEventModalOpen(false)}
        onSave={handleAddEvent}
        eventData={newEvent}
        setEventData={setNewEvent}
        users={users}
      />

      <EditEventModal
        open={editEventModalOpen}
        onClose={() => setEditEventModalOpen(false)}
        onSave={handleEditEvent}
        onDelete={handleDeleteEvent}
        eventData={selectedEvent}
        setEventData={setSelectedEvent}
      />
    </Box>
  );
};

export default Calendar;
